import React, { useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";
import { cn } from "~/lib/utils";

type BlockType = "skill" | "mention" | "tag";

interface BlockPatternConfig {
  type: BlockType;
  pattern: RegExp;
  className: string;
  trigger?: string; // Optional trigger character (e.g., "/", "@", "#")
}

interface TextBlock {
  type: "text" | BlockType;
  content: string;
}

interface RichTextInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  maxHeight?: number;
  className?: string;
  blockPatterns: BlockPatternConfig[];
  onTrigger?: (
    trigger: string,
    blockType: BlockType,
    position: { top: number; left: number },
    searchText: string
  ) => void;
  onTriggerClose?: () => void;
}

export interface RichTextInputRef {
  focus: () => void;
  focusAtEnd: () => void;
}

const ZERO_WIDTH_SPACE = "\u200B";

/**
 * RichTextInput - ContentEditable Rich Text Editor with Inline Blocks
 *
 * ARCHITECTURE OVERVIEW:
 * This component uses a "controlled external update" pattern for rendering blocks.
 * Blocks (like /skills, @mentions, #tags) only appear when the parent explicitly
 * sets the value, NOT while the user is actively typing.
 *
 * DATA FLOW:
 * 1. User types "/skill" → Raw text appears in contenteditable
 * 2. User selects from autocomplete → Parent calls setInputValue("/skill ")
 * 3. Value change from parent triggers useEffect (line 464)
 * 4. useEffect parses value and renders "/skill" as a styled non-editable span
 *
 * WHY THIS PATTERN?
 * - Blocks only appear when "committed" (user selects from autocomplete/dropdown)
 * - Avoids re-rendering contenteditable on every keystroke (would cause cursor jumps)
 * - Gives parent full control over when text becomes a block
 * - Maintains standard React controlled component pattern
 *
 * KEY CHALLENGES SOLVED:
 * 1. Cursor Management: Browser cursor gets "stuck" at non-editable element boundaries.
 *    Solution: Zero-width spaces around text + manual cursor save/restore (lines 164-229)
 *
 * 2. Block Deletion: Need to delete entire block as single unit with 2 backspaces.
 *    Solution: Custom backspace handler with range comparison (lines 374-428)
 *
 * 3. Trigger Detection: Detect "/" without false positives like "1/4" or URLs.
 *    Solution: Only trigger if "/" is first char or preceded by whitespace (lines 282-361)
 *
 * 4. Plain Text Extraction: Need to get clean text from DOM with blocks and zero-width spaces.
 *    Solution: TreeWalker to traverse and filter (lines 232-262)
 *
 * ADDING NEW BLOCK TYPES:
 * Just add to blockPatterns array in parent component:
 * {
 *   type: "mention",
 *   pattern: /@[\w-]+/g,
 *   className: "bg-blue-100 text-blue-800 ...",
 *   trigger: "@"
 * }
 *
 * MAINTENANCE NOTES:
 * - The cursor save/restore logic (lines 164-229) is complex but necessary. Test carefully
 *   if modifying, as cursor position bugs are subtle and hard to debug.
 * - Zero-width spaces are used for cursor positioning. They MUST be filtered when
 *   extracting plain text or they'll appear in the final output.
 * - The "external change" check (line 479) is critical - removing it will cause
 *   infinite render loops.
 */
export const RichTextInput = forwardRef<RichTextInputRef, RichTextInputProps>((props, ref) => {
  const {
    value,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    placeholder,
    maxHeight = 120,
    className,
    blockPatterns,
    onTrigger,
    onTriggerClose,
  } = props;

  const editorRef = useRef<HTMLDivElement>(null);
  const isComposingRef = useRef(false);
  const lastValueRef = useRef(value);

  // ============================================================================
  // UTILITY: Cursor Movement
  // ============================================================================

  /**
   * Moves cursor to the end of the contenteditable content.
   * Used after programmatic updates to position cursor naturally.
   */
  const moveCursorToEnd = useCallback(() => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false); // false = collapse to end
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  // Expose methods via ref
  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editorRef.current?.focus();
      },
      focusAtEnd: () => {
        editorRef.current?.focus();
        // Use setTimeout to ensure focus happens before cursor positioning
        setTimeout(() => {
          moveCursorToEnd();
        }, 0);
      },
    }),
    [moveCursorToEnd]
  );

  // ============================================================================
  // PARSING: Convert plain text string into blocks
  // ============================================================================

  /**
   * Parses a plain text string into an array of text and block elements.
   *
   * HOW IT WORKS:
   * 1. Runs all blockPattern regexes against the text
   * 2. Filters matches to only those at start OR preceded by whitespace
   *    (prevents "1/4" or "email@domain.com" from becoming blocks)
   * 3. Sorts matches by position
   * 4. Builds array alternating between text chunks and block elements
   *
   * EXAMPLE:
   * Input:  "hello /skill-name and @user test"
   * Output: [
   *   { type: "text", content: "hello " },
   *   { type: "skill", content: "/skill-name" },
   *   { type: "text", content: " and " },
   *   { type: "mention", content: "@user" },
   *   { type: "text", content: " test" }
   * ]
   *
   * @param text - Plain text string to parse
   * @returns Array of text/block elements
   */
  const parseValue = useCallback(
    (text: string): TextBlock[] => {
      // Collect all matches from all patterns with their types
      interface Match {
        index: number;
        text: string;
        blockType: BlockType;
      }

      const allMatches: Match[] = [];

      blockPatterns.forEach((config) => {
        const matches = Array.from(text.matchAll(new RegExp(config.pattern.source, "g")));
        matches.forEach((match) => {
          const matchIndex = match.index!;
          const matchText = match[0];

          // Only treat as a block if:
          // 1. It's at the start of the text, OR
          // 2. It's preceded by whitespace
          const isAtStart = matchIndex === 0;
          const charBefore = matchIndex > 0 ? text[matchIndex - 1] : "";
          const hasWhitespaceBefore = /\s/.test(charBefore);

          if (isAtStart || hasWhitespaceBefore) {
            allMatches.push({
              index: matchIndex,
              text: matchText,
              blockType: config.type,
            });
          }
        });
      });

      // Sort matches by index
      allMatches.sort((a, b) => a.index - b.index);

      // Build blocks array
      const blocks: TextBlock[] = [];
      let lastIndex = 0;

      allMatches.forEach((match) => {
        // Add text before match
        if (match.index > lastIndex) {
          blocks.push({ type: "text", content: text.slice(lastIndex, match.index) });
        }

        // Add block with specific type
        blocks.push({ type: match.blockType, content: match.text });

        lastIndex = match.index + match.text.length;
      });

      // Add remaining text
      if (lastIndex < text.length) {
        blocks.push({ type: "text", content: text.slice(lastIndex) });
      }

      // Return empty array if no text (allows CSS placeholder to show)
      return blocks.length > 0 ? blocks : text ? [{ type: "text", content: text }] : [];
    },
    [blockPatterns]
  );

  // ============================================================================
  // CURSOR MANAGEMENT: Save & Restore
  // ============================================================================
  //
  // WHY THIS IS COMPLEX:
  // We insert zero-width spaces (\u200B) around text nodes to help with cursor
  // positioning at block boundaries. But we need to track cursor position in
  // "visible character space" (excluding those zero-width spaces) so it remains
  // stable across re-renders.
  //
  // EXAMPLE:
  // DOM:      "hello \u200B" + <span>/skill</span> + "\u200B world"
  // Visible:  "hello /skill world"
  // If cursor is after "hello " → position is 6 (not 7, ignoring zero-width)
  // ============================================================================

  /**
   * Saves current cursor position as a character offset in "visible character space".
   *
   * @returns Character offset (excluding zero-width spaces), or null if no selection
   */
  const saveCursorPosition = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current!);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    // Count characters excluding zero-width spaces
    const text = preCaretRange.toString();
    return text.replace(/\u200B/g, "").length;
  }, []);

  /**
   * Restores cursor to a specific character offset in "visible character space".
   *
   * HOW IT WORKS:
   * 1. Walk through all text nodes with TreeWalker
   * 2. Count visible characters (skip zero-width spaces)
   * 3. When we reach the target position, map back to actual DOM offset
   * 4. Set selection range at that position
   *
   * @param position - Character offset in visible character space
   */
  const restoreCursorPosition = useCallback((position: number) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    let currentPos = 0;
    let targetNode: Node | null = null;
    let targetOffset = 0;

    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT, null);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const text = node.textContent || "";

      // Count visible characters only (skip zero-width spaces)
      const visibleText = text.replace(/\u200B/g, "");
      const visibleLength = visibleText.length;

      if (currentPos + visibleLength >= position) {
        targetNode = node;

        // Map from visible position to actual position in text node
        const visibleOffset = position - currentPos;
        let actualOffset = 0;
        let visibleCount = 0;

        for (let i = 0; i < text.length && visibleCount < visibleOffset; i++) {
          if (text[i] !== "\u200B") {
            visibleCount++;
          }
          actualOffset = i + 1;
        }

        targetOffset = actualOffset;
        break;
      }

      currentPos += visibleLength;
    }

    if (targetNode) {
      const range = document.createRange();
      range.setStart(targetNode, Math.min(targetOffset, targetNode.textContent?.length || 0));
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, []);

  // ============================================================================
  // TEXT EXTRACTION: Get plain text from DOM
  // ============================================================================

  /**
   * Extracts plain text from the contenteditable DOM.
   *
   * CHALLENGES:
   * - DOM contains both text nodes AND non-editable span elements (blocks)
   * - Text nodes may contain zero-width spaces that must be filtered
   * - Block spans contain the block text (e.g., "/skill") that must be included
   * - Must avoid duplicating text (span's textContent vs its child text nodes)
   *
   * APPROACH:
   * Use TreeWalker to traverse all text nodes and elements, collecting:
   * - Text nodes (excluding those inside block spans, to avoid duplication)
   * - Block span elements (include their textContent)
   * - Filter out zero-width spaces from text nodes
   *
   * @returns Plain text string suitable for parent component state
   */
  const extractPlainText = useCallback((): string => {
    if (!editorRef.current) return "";

    let text = "";
    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      null
    );

    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        // Skip text nodes that are children of block spans (to avoid duplication)
        const parent = node.parentElement;
        if (parent?.hasAttribute("data-block-type")) {
          continue;
        }
        const content = node.textContent || "";
        // Filter out zero-width spaces
        text += content.replace(/\u200B/g, "");
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.hasAttribute("data-block-type")) {
          text += element.textContent || "";
        }
      }
    }

    return text;
  }, []);

  // ============================================================================
  // INPUT HANDLING: Process user input and trigger detection
  // ============================================================================

  /**
   * Handles all input changes from the contenteditable element.
   *
   * RESPONSIBILITIES:
   * 1. Extract plain text from DOM and notify parent via onChange
   * 2. Auto-resize the input based on content
   * 3. Detect trigger characters (/, @, #) and notify parent via onTrigger
   *
   * TRIGGER DETECTION LOGIC:
   * - Only trigger if trigger char is first in input OR preceded by whitespace
   * - This prevents false positives like "1/4" or "email@domain.com"
   * - Anchors popup at the trigger character position (not cursor)
   * - Passes searchText (everything after trigger) for filtering autocomplete
   */
  const handleInput = useCallback(() => {
    if (isComposingRef.current) return;

    const plainText = extractPlainText();

    if (plainText !== lastValueRef.current) {
      lastValueRef.current = plainText;
      onChange(plainText);
    }

    // Auto-resize
    if (editorRef.current) {
      const editor = editorRef.current;
      editor.style.height = "auto";
      editor.style.height = `${Math.min(editor.scrollHeight, maxHeight)}px`;
    }

    // Check for trigger characters
    if (onTrigger) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        // Don't trigger if cursor is inside or immediately after a block
        const container = range.startContainer;
        const parentElement =
          container.nodeType === Node.TEXT_NODE
            ? container.parentElement
            : (container as HTMLElement);

        // Check if we're inside a block
        if (parentElement?.hasAttribute?.("data-block-type")) {
          onTriggerClose?.();
          return;
        }

        // Don't trigger if cursor is immediately after a block
        if (range.collapsed && range.startOffset === 0 && container.previousSibling) {
          const prev = container.previousSibling;
          if ((prev as HTMLElement).hasAttribute?.("data-block-type")) {
            onTriggerClose?.();
            return;
          }
        }

        // Only check for triggers in actual text nodes
        if (container.nodeType !== Node.TEXT_NODE) {
          onTriggerClose?.();
          return;
        }

        const textBeforeCursor = container.textContent?.slice(0, range.startOffset) || "";

        // Check for any trigger character from blockPatterns
        const triggers = blockPatterns.map((p) => p.trigger).filter((t): t is string => !!t);

        let foundTrigger = false;

        for (const trigger of triggers) {
          const lastTriggerIndex = textBeforeCursor.lastIndexOf(trigger);
          if (lastTriggerIndex !== -1) {
            // Only trigger if:
            // 1. It's the first character, OR
            // 2. There's whitespace before the trigger
            const isFirstChar = lastTriggerIndex === 0;
            const charBeforeTrigger =
              lastTriggerIndex > 0 ? textBeforeCursor[lastTriggerIndex - 1] : "";
            const hasWhitespaceBefore = /\s/.test(charBeforeTrigger);

            if (isFirstChar || hasWhitespaceBefore) {
              const searchText = textBeforeCursor.slice(lastTriggerIndex + trigger.length);

              // Create a range at the trigger position to anchor the popup there
              const triggerRange = document.createRange();
              triggerRange.setStart(range.startContainer, lastTriggerIndex);
              triggerRange.setEnd(range.startContainer, lastTriggerIndex + trigger.length);
              const rect = triggerRange.getBoundingClientRect();

              const position = { top: rect.top, left: rect.left };

              // Find the block type for this trigger
              const blockType = blockPatterns.find((p) => p.trigger === trigger)?.type || "skill";

              onTrigger(trigger, blockType, position, searchText);

              foundTrigger = true;
              return;
            }
          }
        }

        // No trigger found - close popup
        if (!foundTrigger) {
          onTriggerClose?.();
        }
      }
    }
  }, [extractPlainText, onChange, maxHeight, onTrigger, onTriggerClose, blockPatterns]);

  // ============================================================================
  // KEYBOARD HANDLING: Block deletion and special keys
  // ============================================================================

  /**
   * Handles keyboard events, primarily for block deletion.
   *
   * BLOCK DELETION UX (Backspace):
   * When cursor is right after a block with no text between:
   * - First backspace: Deletes the trailing space (normal behavior)
   * - Second backspace: Deletes the entire block as a unit
   *
   * HOW IT WORKS:
   * 1. Find all blocks in the editor
   * 2. Check if any block is immediately before cursor (no text between)
   * 3. If yes, delete block and restore cursor position
   * 4. Use Range.compareBoundaryPoints to determine block-cursor relationship
   *
   * DELETE KEY:
   * Similar logic but for blocks immediately after cursor
   *
   * ESCAPE KEY:
   * Closes any open autocomplete popups
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isComposingRef.current) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      // Handle backspace - delete block if cursor is right after it with no text between
      if (e.key === "Backspace" && range.collapsed) {
        // Get all blocks in the editor
        const blocks = editorRef.current?.querySelectorAll("[data-block-type]") || [];

        for (let i = blocks.length - 1; i >= 0; i--) {
          const block = blocks[i];
          const blockRange = document.createRange();
          blockRange.selectNode(block);

          // Compare block position to cursor position using Range API
          // comparison < 0 means block ends before cursor starts
          const comparison = blockRange.compareBoundaryPoints(Range.END_TO_START, range);

          if (comparison < 0) {
            // Block is before cursor - check if there's only whitespace between
            const betweenRange = document.createRange();
            betweenRange.setStartAfter(block); // Start AFTER block (not inside it)
            betweenRange.setEnd(range.startContainer, range.startOffset);

            const textBetween = betweenRange.toString();

            // Remove zero-width spaces to see what's actually there
            const actualText = textBetween.replace(/\u200B/g, "");

            // UX: Only delete block if there's literally nothing (not even a space).
            // If there's a space, let browser delete it normally (first backspace).
            // Second backspace (when actualText.length === 0) deletes the block.
            if (actualText.length === 0) {
              e.preventDefault();

              // Save current cursor position in plain text
              const cursorPos = saveCursorPosition();
              const blockLength = block.textContent?.length || 0;

              // Remove the block
              block.remove();

              // Trigger re-render
              handleInput();

              // Restore cursor to where it was, minus the deleted block length
              if (cursorPos !== null) {
                setTimeout(() => {
                  restoreCursorPosition(cursorPos - blockLength);
                }, 0);
              }

              return;
            }

            // Found a block with text after it, stop checking
            break;
          }
        }
      }

      // Handle delete - only delete block if cursor is immediately before it
      if (e.key === "Delete" && range.collapsed) {
        const container = range.startContainer;
        const offset = range.startOffset;

        // Check if we're at the end of a text node
        if (container.nodeType === Node.TEXT_NODE) {
          const textLength = container.textContent?.length || 0;
          if (offset === textLength) {
            const nextNode = container.nextSibling;
            if (nextNode && (nextNode as HTMLElement).hasAttribute?.("data-block-type")) {
              e.preventDefault();
              nextNode.remove();
              handleInput();
              return;
            }
          }
        }
      }

      // Close popups on Escape
      if (e.key === "Escape") {
        onTriggerClose?.();
      }

      // Call parent keydown handler
      if (onKeyDown) {
        onKeyDown(e);
      }
    },
    [handleInput, onKeyDown, onTriggerClose, saveCursorPosition, restoreCursorPosition]
  );

  // ============================================================================
  // RENDERING: Convert value to DOM blocks
  // ============================================================================

  /**
   * Renders blocks when value changes externally (from parent component).
   *
   * CRITICAL: Only renders when value comes from parent, NOT during user typing.
   * This is the "controlled external update" pattern.
   *
   * WHY THE CHECK AT LINE 479?
   * - value !== plainText: Parent set new value that differs from current DOM
   * - value !== lastValueRef.current: Prevents re-rendering same value twice
   *
   * RENDERING PROCESS:
   * 1. Save current cursor position
   * 2. Parse value into text and block elements
   * 3. Clear contenteditable innerHTML
   * 4. Create DOM nodes for each element:
   *    - Text: text node with zero-width spaces for cursor positioning
   *    - Block: non-editable span with className from blockPattern config
   * 5. Restore cursor position
   * 6. Auto-resize based on new content height
   *
   * ZERO-WIDTH SPACES:
   * Added around text nodes (not at very start/end) to help cursor positioning
   * at block boundaries. Without these, cursor gets "stuck" and can't move past blocks.
   */
  useEffect(() => {
    if (!editorRef.current || isComposingRef.current) {
      return;
    }

    const plainText = extractPlainText();

    // Always clear innerHTML if value is empty (to show placeholder)
    if (value === "" && editorRef.current.innerHTML !== "") {
      editorRef.current.innerHTML = "";
      lastValueRef.current = value;
      return;
    }

    // CRITICAL CHECK: Only render blocks when value changes from parent (external).
    // This prevents infinite loops and avoids re-rendering while user is typing.
    // Without this, every keystroke would trigger a re-render → cursor would jump.
    if (value !== plainText && value !== lastValueRef.current) {
      const cursorPos = saveCursorPosition();
      const blocks = parseValue(value);

      // Render blocks
      editorRef.current.innerHTML = "";

      // Leave div empty if no blocks (allows CSS placeholder to show)
      if (blocks.length === 0) {
        lastValueRef.current = value;
        return;
      }

      blocks.forEach((block, index) => {
        if (block.type === "text") {
          // Add zero-width spaces around text (not at very start/end) for cursor positioning.
          // These spaces allow cursor to move smoothly past block boundaries.
          // They MUST be filtered out when extracting plain text (see extractPlainText).
          const textNode = document.createTextNode(
            (index === 0 ? "" : ZERO_WIDTH_SPACE) +
              block.content +
              (index === blocks.length - 1 ? "" : ZERO_WIDTH_SPACE)
          );
          editorRef.current!.appendChild(textNode);
        } else {
          // Render block as non-editable span with styling from blockPattern config
          const patternConfig = blockPatterns.find((p) => p.type === block.type);
          const className =
            patternConfig?.className ||
            "text-block bg-gray-200 text-gray-800 rounded px-1.5 py-0.5 inline-block mx-0.5";

          const span = document.createElement("span");
          span.className = className;
          span.contentEditable = "false"; // Critical: prevents editing inside block
          span.textContent = block.content;
          span.setAttribute("data-block-type", block.type); // For querying blocks in deletion logic
          editorRef.current!.appendChild(span);
        }
      });

      if (cursorPos !== null) {
        restoreCursorPosition(cursorPos);
      }

      lastValueRef.current = value;

      // Auto-resize
      editorRef.current.style.height = "auto";
      editorRef.current.style.height = `${Math.min(editorRef.current.scrollHeight, maxHeight)}px`;
    }
  }, [
    value,
    parseValue,
    extractPlainText,
    saveCursorPosition,
    restoreCursorPosition,
    maxHeight,
    blockPatterns,
  ]);

  return (
    <div
      ref={editorRef}
      contentEditable
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      onCompositionStart={() => {
        isComposingRef.current = true;
      }}
      onCompositionEnd={() => {
        isComposingRef.current = false;
        handleInput();
      }}
      className={cn(
        "flex-1 font-normal text-foreground text-sm text-left bg-transparent border-0 outline-none min-h-[24px] p-0 resize-none focus:outline-none shadow-none overflow-y-auto whitespace-pre-wrap break-words cursor-text",
        "empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400",
        className
      )}
      style={{ maxHeight: `${maxHeight}px` }}
      data-placeholder={placeholder}
      role="textbox"
      aria-multiline="true"
      aria-label="Chat message input"
      suppressContentEditableWarning
    />
  );
});

RichTextInput.displayName = "RichTextInput";
